// /Users/boyd/Dev/freegopos/einvapp/src/pages/InvoiceDetails.tsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    fetchInvoiceDetails,
    downloadOrderPdf,
    sendWhatsAppMessage,
    sendOrderEmail,
    sendInvoiceEmail
} from '../utils/api';
import { Invoice, Modifier, InvoiceItem, InvoiceSummary, displayPrice } from '../utils/types';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
// import { CustomerContext, CustomerContextProps } from '../context/customerContext';
import { useCustomerContext } from '../context/customerContext';

// Access API URL for constructing the link
const API_URL = window.APP_CONFIG?.API_URL || window.APP_CONFIG?.REACT_APP_API_URL;

const InvoiceDetails: React.FC = () => {
    const navigate = useNavigate();
    const { invoice_id } = useParams<{ invoice_id: string }>();
    const [invoice, setInvoice] = useState<Invoice | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    // const { invoiceId } = useParams<{ invoiceId: string }>();

    const { customers } = useCustomerContext();
    const [isDownloadingPdf, setIsDownloadingPdf] = useState<boolean>(false);
    const [isSendingWhatsApp, setIsSendingWhatsApp] = useState<boolean>(false);
    const [isSendingEmail, setIsSendingEmail] = useState<boolean>(false);

    useEffect(() => {
        const fetchInvoice = async () => {
            if (!invoice_id) return;
            setLoading(true);
            setError(null);
            try {
                const invoiceData = await fetchInvoiceDetails(parseInt(invoice_id));
                setInvoice(invoiceData);
            } catch (err: any) {
                console.error('Error fetching invoice:', err);
                setError(err.message || 'Failed to fetch invoice details');
                toast.error('Failed to fetch invoice details. Please try again.');
            } finally {
                setLoading(false);
            }
        };
        fetchInvoice();
    }, [invoice_id]);

    // --- Function to navigate and pass state ---
    const goToMyinvoisPage = (invoiceData: Invoice) => {
        console.log(`Navigating to Myinvois page for invoice ID: ${invoiceData.id}`);
        // Pass the invoice data in the state object
        navigate(`/myinvois/${invoiceData.id}`, { state: { invoiceData: invoiceData } });
    };
    // --- End of function ---


    // --- Handle PDF Download ---
    const handleDownloadPdf = async () => {
        if (!invoice?.invoice_uuid) {
            toast.error("Invoice UUID is missing.");
            return;
        }
        setIsDownloadingPdf(true);
        try {
            // Directly fetch the PDF using the new route
            const pdfUrl = `${API_URL}/invoices/${invoice.invoice_uuid}/pdf`;
            const response = await fetch(pdfUrl, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                }
            });

            if (!response.ok) {
                // Try to parse error message from JSON
                let errorMsg = `Failed to download Invoice PDF: ${response.statusText}`;
                try {
                    const errorData = await response.json();
                    errorMsg = errorData.message || errorMsg;
                } catch { }
                throw new Error(errorMsg);
            }

            const pdfBlob = await response.blob();
            const url = window.URL.createObjectURL(pdfBlob);

            // const link = document.createElement('a');
            // link.href = url;
            // link.setAttribute('download', `invoice_${invoice.invoice_uuid}.pdf`);
            // document.body.appendChild(link);
            // link.click();
            // link.parentNode?.removeChild(link);
            // window.URL.revokeObjectURL(url);
            // toast.success("PDF download started.");

            // Open PDF in a new tab
            window.open(url, '_blank');

            // Optionally, revoke the object URL after some time
            setTimeout(() => window.URL.revokeObjectURL(url), 10000);

            toast.success("PDF opened in new tab.");

        } catch (error: any) {
            console.error('Error downloading PDF:', error);
            toast.error(`Failed to download Invoice PDF: ${error.message || 'Check console for details.'}`);
        } finally {
            setIsDownloadingPdf(false);
        }
    };

    // --- Handle Sending WhatsApp Link ---
    // console.log('invoiceId:', invoice_id);
    // console.log('invoice:', invoice);

    const handleSendWhatsAppLink = async () => {


        if (!invoice_id || !invoice) {
            toast.error("Invoice are missing for WhatsApp.");
            return;
        }

        // const customerForNotification = customers.find(c => c.id === invoice.customer_id);
        const customerForNotification = customers?.find(c => String(c.id) === String(invoice.customer_id));

        let targetPhoneNumber = customerForNotification?.phone;
        if (targetPhoneNumber && targetPhoneNumber.startsWith('0')) {
            targetPhoneNumber = '6' + targetPhoneNumber;
        }

        if (!targetPhoneNumber) {
            toast.warn(`Cannot send Invoice WhatsApp: Phone number for customer ${customerForNotification?.name || invoice.customer_id} not found.`);
            return;
        }

        const pdfLink = `${API_URL}/invoices/${invoice?.invoice_uuid}/pdf`;

        // Ensure phone number format is correct for your venom-bot service
        // const targetPhoneNumber = '601112163808'; //'60188748808'; // Example, adjust as needed
        if (!invoice?.invoice_uuid) {
            toast.error("Invoice UUID is missing, cannot generate PDF link for WhatsApp.");
            return;
        }

        setIsSendingWhatsApp(true);

        try {
            const settings = JSON.parse(localStorage.getItem('settings') || '{}');

            const whatsappMessage = `*${settings.shortname || 'Our Store'}* sent you a document.\n\n\nInvoice *${invoice_id}* PDF is ready.\n\nDownload here: ${pdfLink}\n\n\n*Note: This is a bot message, please do not reply.*\n\n*Please contact ${settings.shortname || 'Us'} at ${settings.phone || 'our contact number'} instead.*`;

            await sendWhatsAppMessage(targetPhoneNumber, whatsappMessage);
            // toast.info(`WhatsApp link sent to ${targetPhoneNumber}.`);
            toast.info(`Invoice PDF link sent via WhatsApp to ${customerForNotification?.name} (${targetPhoneNumber}).`);


        } catch (whatsappError: any) {
            console.error('Failed to send Invoice WhatsApp message:', whatsappError);
            toast.error(`Failed to send Invoice WhatsApp link: ${whatsappError.message}`);
        } finally {
            setIsSendingWhatsApp(false);
        }
    };


    // const pdfLink = `${API_URL}/invoices/${invoice_id}/pdf`;
    // const pdfLink = `${API_URL}/invoices/${invoice?.invoice_uuid}/pdf`;
    // const targetPhoneNumber = '601112163808'; // Ensure correct format
    //     const settings = JSON.parse(localStorage.getItem('settings') || '{}');
    //     const whatsappMessage = `*${settings.shortname}*\n\nInvoice *${invoice_id}* PDF is ready.\n\nDownload here: ${pdfLink}`;


    // --- Handle Sending Invoice Email ---
    const handleSendEmail = async () => {
        if (!invoice_id || !invoice) {
            toast.error("Invoice details are missing for Email.");
            return;
        }

        // const customerForNotification = customers.find(c => c.id === invoice.customer_id);
        const customerForNotification = customers?.find(c => String(c.id) === String(invoice.customer_id));
        const recipientEmail = customerForNotification?.email;

        if (!recipientEmail) {
            toast.warn(`Cannot send Invoice email: Email address for customer ${customerForNotification?.name || invoice.customer_id} not found.`);
            return;
        }

        setIsSendingEmail(true);
        try {
            const response = await sendInvoiceEmail(parseInt(invoice_id), recipientEmail);
            toast.success(response.message || `Invoice confirmation email sent to ${customerForNotification.name} (${recipientEmail}).`);
        } catch (emailError: any) {
            console.error('Failed to send invoice email:', emailError);
            toast.error(`Failed to send Invoice email: ${emailError.message}`);
        } finally {
            setIsSendingEmail(false);
        }
    };

    if (loading) {
        return <div className="container mx-auto p-4">Loading invoice details...</div>;
    }
    if (error) {
        return <div className="container mx-auto p-4 text-red-500">Error: {error}</div>;
    }
    if (!invoice) {
        return <div className="container mx-auto p-4">Invoice not found.</div>;
    }

    // --- Determine if any background action is in progress ---
    const isActionInProgress = isDownloadingPdf || isSendingWhatsApp || isSendingEmail;

    return (
        <div className="container mx-auto p-4">
            <h1 className="text-2xl font-bold mb-4">Invoice Details</h1>


            {/* --- Action Buttons Row 1 (Download/Send) --- */}
            <div className="mb-4 flex flex-wrap gap-2">
                <button
                    onClick={handleDownloadPdf}
                    disabled={isActionInProgress}
                    className={`bg-purple-600 hover:bg-purple-700 text-white font-bold py-1 px-3 rounded transition duration-150 ${isActionInProgress ? 'opacity-50 cursor-not-allowed' : ''}`}
                    title="Download the order details as a PDF file."
                >
                    {isDownloadingPdf ? 'Downloading...' : 'Download Invoice PDF'}
                </button>

                <button
                    onClick={handleSendWhatsAppLink}
                    disabled={isActionInProgress}
                    className={`bg-teal-600 hover:bg-teal-700 text-white font-bold py-1 px-3 rounded transition duration-150 ${isActionInProgress ? 'opacity-50 cursor-not-allowed' : ''}`}
                    title="Send a WhatsApp message with a link to the PDF."
                >
                    {isSendingWhatsApp ? 'Sending Invoice WhatsApp...' : 'Send Invoice WhatsApp to Customer'}
                </button>

                <button
                    onClick={handleSendEmail}
                    disabled={isActionInProgress}
                    className={`bg-cyan-600 hover:bg-cyan-700 text-white font-bold py-1 px-3 rounded transition duration-150 ${isActionInProgress ? 'opacity-50 cursor-not-allowed' : ''}`}
                    title="Send the Invoice details via email."
                >
                    {isSendingEmail ? 'Sending Invoice Email...' : 'Send Invoice Email to Customer'}
                </button>
            </div>


            {/* <div className="w-1/2 md:w-2/5 text-xs flex justify-between p-4">
                <button
                    className={`text-white bg-blue-600 mx-2 px-2 py-1 rounded-lg mb-4`}
                    // Call the navigation function, passing the current invoice data
                    onClick={() => goToMyinvoisPage(invoice)}
                >
                    e-invoice
                </button>
            </div> */}

            {/* ... rest of the component ... */}
            <p><strong>Invoice ID : </strong>
                <Link to={`/invoice-history`} className='text-blue-600 hover:underline'>
                    {invoice.id}
                </Link>
            </p>
            <p><strong>Order ID : </strong>
                <Link to={`/order/${invoice.order_id}`} className='text-blue-600 hover:underline'>
                    {invoice.order_id}
                </Link>
            </p>
            <p><strong>Customer Name : </strong>
                &nbsp;{invoice.customer_id}&nbsp;:&nbsp;
                &nbsp;{Array.isArray(customers) && customers.find(c => String(c.id) === String(invoice.customer_id))?.name || 'Unknown Customer'}
                &nbsp;&lt;{Array.isArray(customers) && customers.find(c => String(c.id) === String(invoice.customer_id))?.email || 'Unknown Email'}
                &gt;
            </p>
            <p><strong>Total Amount (Pre-tax):</strong> {invoice.credit_pretax_amt}</p>
            <h2 className="text-xl font-bold mt-4">Invoice Items</h2>
            <ul className="list-disc list-inside">
                {invoice.items.map((item, index) => (
                    <li key={index} className="py-4 flex items-center justify-between before:content-[counter(my-counter)] before:mr-4" style={{ counterIncrement: 'my-counter' }}>
                        <div className="flex-grow">
                            <p className="font-medium">
                                <Link to={`/product/${item.product_id}`} target='_blank'>
                                    {item.payment_for}
                                    <span className="text-xs md:text-xs text-gray-500 pl-2">sku: {item.payment_desc}</span>
                                </Link>
                                &nbsp;({displayPrice(Number(item.product_value))})
                            </p>
                            {typeof item.modifiers === 'string' && (item.modifiers = JSON.parse(item.modifiers))}
                            {Array.isArray(item.modifiers) && item.modifiers.length > 0 && ( // Check if it's an array
                                <div className='modifiers-div'>
                                    <ul className="list-disc list-inside ">
                                        {item.modifiers.map((modifier: Modifier) => (
                                            <li key={modifier.id} className="text-xs">
                                                {modifier.product_name}
                                                <span className="text-xs md:text-xs text-gray-500 pl-2">sku: {modifier.product_code}</span>
                                                ({displayPrice(modifier.product_value)})
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            <p className="text-sm text-gray-500">
                                {displayPrice(item.unit_price)} x {item.qty} = {displayPrice(item.unit_price * item.qty)}
                            </p>
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
};

export default InvoiceDetails;
